import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../api/client";

export type User = {
  id: string;
  email: string;
  role: "learner" | "admin";
  profile?: { fullName?: string };
};

type AuthContextType = {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; message?: string }>;
  signup: (email: string, password: string, fullName?: string) => Promise<{ success: boolean; message?: string }>;
  logout: () => void;
  refreshUser: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      if (data.success && data.user) {
        setUser({ ...data.user, id: data.user.id || data.user._id });
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
      localStorage.removeItem("token");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const login = async (email: string, password: string) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.success && data.token) {
        localStorage.setItem("token", data.token);
        setUser(data.user ? { ...data.user, id: data.user.id || data.user._id } : null);
        return { success: true };
      }
      return { success: false, message: data.message || "Login failed." };
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      return { success: false, message: msg || "Login failed." };
    }
  };

  const signup = async (email: string, password: string, fullName?: string) => {
    try {
      const { data } = await api.post("/auth/signup", { email, password, fullName });
      if (data.success && data.token) {
        localStorage.setItem("token", data.token);
        setUser(data.user ? { ...data.user, id: data.user.id || data.user._id } : null);
        return { success: true };
      }
      return { success: false, message: data.message || "Sign up failed." };
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      return { success: false, message: msg || "Sign up failed." };
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
